import { call, put, takeLatest } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import request, { type RequestError } from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { handleAxiosError } from "../../../utils/APIValidations";
import { COLORS } from "../../../utils/colors";
import { loadAnalytics, analyticsLoaded, loadAnalyticsError } from "./slice";
import type {
  AnalyticsResponse,
  AnalyticsSeries,
  AppointmentAnalyticsResponse,
  CallChartData,
  ChartPoint,
  LoadAnalyticsPayload,
} from "./types";

const EMPTY_SERIES: AnalyticsSeries = {
  total: 0,
  change_percentage: 0,
  chart: [],
};

const EMPTY_CALLS_RESPONSE: AnalyticsResponse = {
  successful_calls: EMPTY_SERIES,
  unsuccessful_calls: EMPTY_SERIES,
};

const EMPTY_APPOINTMENTS_RESPONSE: AppointmentAnalyticsResponse = {
  scheduled_appointments: EMPTY_SERIES,
  rescheduled_appointments: EMPTY_SERIES,
  cancelled_appointments: EMPTY_SERIES,
  unsuccessful_appointments: EMPTY_SERIES,
};

function isNoData(e: unknown): boolean {
  return (e as RequestError)?.status === 404;
}

// Daily buckets arrive as YYYY-MM-DD, monthly (year filter) as YYYY-MM.
function formatLabel(dateStr: string): string {
  return dateStr.length === 7
    ? dayjs(dateStr).format("MMM")
    : dayjs(dateStr).format("MMM D");
}

function toPoints(series: AnalyticsSeries): ChartPoint[] {
  return series.chart.map((p) => ({ label: formatLabel(p.date), value: p.count }));
}

function lastIndexOf(points: ChartPoint[]): number {
  return Math.max(points.length - 1, 0);
}

function mapCallsResponse(response: AnalyticsResponse): CallChartData[] {
  const successful = response.successful_calls ?? EMPTY_SERIES;
  const unsuccessful = response.unsuccessful_calls ?? EMPTY_SERIES;

  const successPoints = toPoints(successful);
  const failPoints = toPoints(unsuccessful);

  return [
    {
      key: "successful",
      title: "Successful Calls",
      subtitle: "Calls resolved successfully",
      total: successful.total,
      valueLabel: "Calls",
      strokeColor: COLORS.chartSuccessLine,
      fillColor: COLORS.chartSuccessFill,
      valueColor: COLORS.chartSuccessLine,
      gradientId: "analytics-successful-gradient",
      defaultIndex: lastIndexOf(successPoints),
      points: successPoints,
    },
    {
      key: "unsuccessful",
      title: "Unsuccessful Calls",
      subtitle: "Calls resolved unsuccessfully",
      total: unsuccessful.total,
      valueLabel: "Calls",
      strokeColor: COLORS.chartUnsuccessLine,
      fillColor: COLORS.chartUnsuccessFill,
      valueColor: COLORS.chartUnsuccessLine,
      gradientId: "analytics-unsuccessful-gradient",
      defaultIndex: lastIndexOf(failPoints),
      points: failPoints,
    },
  ];
}

function mapAppointmentsResponse(
  response: AppointmentAnalyticsResponse,
): CallChartData[] {
  const scheduled = response.scheduled_appointments ?? EMPTY_SERIES;
  const rescheduled = response.rescheduled_appointments ?? EMPTY_SERIES;
  const cancelled = response.cancelled_appointments ?? EMPTY_SERIES;
  const unsuccessful = response.unsuccessful_appointments ?? EMPTY_SERIES;

  const scheduledPoints = toPoints(scheduled);
  const rescheduledPoints = toPoints(rescheduled);
  const cancelledPoints = toPoints(cancelled);
  const unsuccessfulPoints = toPoints(unsuccessful);

  return [
    {
      key: "scheduled_appointments",
      title: "Scheduled Appointments",
      subtitle: "New Appointments booked",
      total: scheduled.total,
      valueLabel: "Appts",
      strokeColor: COLORS.chartScheduledLine,
      fillColor: COLORS.chartScheduledFill,
      valueColor: COLORS.chartScheduledLine,
      gradientId: "analytics-scheduled-gradient",
      defaultIndex: lastIndexOf(scheduledPoints),
      points: scheduledPoints,
    },
    {
      key: "rescheduled_appointments",
      title: "Rescheduled Appointments",
      subtitle: "Appointments moved to a new time and date",
      total: rescheduled.total,
      valueLabel: "Appts",
      strokeColor: COLORS.chartRescheduledLine,
      fillColor: COLORS.chartRescheduledFill,
      valueColor: COLORS.chartRescheduledLine,
      gradientId: "analytics-rescheduled-gradient",
      defaultIndex: lastIndexOf(rescheduledPoints),
      points: rescheduledPoints,
    },
    {
      key: "cancelled_appointments",
      title: "Cancelled Appointments",
      subtitle: "Appointments cancelled by the patient or clinic",
      total: cancelled.total,
      valueLabel: "Appts",
      strokeColor: COLORS.chartCancelledLine,
      fillColor: COLORS.chartCancelledFill,
      valueColor: COLORS.chartCancelledLine,
      gradientId: "analytics-cancelled-gradient",
      defaultIndex: lastIndexOf(cancelledPoints),
      points: cancelledPoints,
    },
    {
      key: "unsuccessful_appointments",
      title: "Unsuccessful Appointments",
      subtitle: "Calls that did not result in a booking",
      total: unsuccessful.total,
      valueLabel: "Appts",
      strokeColor: COLORS.chartApptUnsuccessLine,
      fillColor: COLORS.chartApptUnsuccessFill,
      valueColor: COLORS.chartApptUnsuccessLine,
      gradientId: "analytics-appt-unsuccessful-gradient",
      defaultIndex: lastIndexOf(unsuccessfulPoints),
      points: unsuccessfulPoints,
    },
  ];
}

export function* fetchAnalytics({
  payload,
}: PayloadAction<LoadAnalyticsPayload | undefined>) {
  const isAppointments = payload?.module === "appointments";

  try {
    const params = new URLSearchParams();
    if (payload?.year) {
      params.set("year", String(payload.year));
    } else {
      if (payload?.startDate) params.set("start_date", payload.startDate);
      if (payload?.endDate) params.set("end_date", payload.endDate);
    }

    const query = params.toString();
    const path = isAppointments
      ? "dashboard/appointment-analytics"
      : "dashboard/analytics";

    const response: AnalyticsResponse & AppointmentAnalyticsResponse =
      yield call(request, `${API_ENDPOINT}${path}${query ? `?${query}` : ""}`);

    yield put(
      analyticsLoaded(
        isAppointments
          ? mapAppointmentsResponse(response)
          : mapCallsResponse(response),
      ),
    );
  } catch (e) {
    if (isNoData(e)) {
      yield put(
        analyticsLoaded(
          isAppointments
            ? mapAppointmentsResponse(EMPTY_APPOINTMENTS_RESPONSE)
            : mapCallsResponse(EMPTY_CALLS_RESPONSE),
        ),
      );
      return;
    }
    yield call(handleAxiosError, e);
    yield put(loadAnalyticsError());
  }
}

export default function* saga() {
  yield takeLatest(loadAnalytics.type, fetchAnalytics);
}
